import {jsPDF as JsPDF} from "jspdf";

/**
 * Prüft ob der Browser Safari ist (nicht Chrome/Chromium).
 * Safari enthält "Safari/" im UA-String, aber Chromium-Browser auch —
 * daher zusätzlich prüfen, dass KEIN "Chrome/" enthalten ist.
 */
function getIsSafari(): boolean {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent;
    return /Safari\//.test(ua) && !/Chrome\//.test(ua);
}

/**
 * Druckt ein jsPDF-Dokument.
 * - Safari: Über versteckten iframe (Inline-Druck, kein neuer Tab).
 *   Beim wiederholten Drucken ohne Seiten-Reload zeigt Safari einen
 *   Bestätigungsdialog — das ist eine Safari-Limitierung.
 * - Alle anderen (Chromium, Firefox): Neuer Tab mit autoPrint().
 *   Chromium: OOPIF macht iframe.contentWindow.print() zum No-Op.
 *   Firefox: PDF.js im hidden iframe rendert leere Seiten beim Drucken.
 */
export function printPdf(doc: JsPDF): void {
    if (getIsSafari()) {
        printViaIframe(doc.output("blob"));
        return;
    }

    // autoPrint() bettet einen OpenAction-Befehl ein, der den Druckdialog
    // beim Öffnen des Tabs automatisch anstößt — kein zusätzliches print().
    doc.autoPrint();
    openInNewTab(doc.output("blob"), false);
}

/**
 * Druckt ein fertiges PDF (z.B. eine vom Server geladene Rechnung).
 * - Safari: versteckter iframe, siehe printPdf().
 * - Alle anderen: neuer Tab. Ohne eingebettetes autoPrint() (das geht nur
 *   bei jsPDF-Dokumenten) versuchen wir print() auf dem neuen Tab; schlägt das
 *   fehl, bleibt der Tab mit dem Browser-PDF-Viewer und dessen Druckfunktion offen.
 */
export function printPdfBlob(blob: Blob): void {
    if (getIsSafari()) {
        printViaIframe(blob);
        return;
    }

    openInNewTab(blob, true);
}

/**
 * Öffnet das PDF in einem neuen Tab. Mit `tryPrint` versuchen wir zusätzlich
 * print() auf dem Tab (für PDFs ohne eingebetteten autoPrint()-Befehl).
 */
function openInNewTab(blob: Blob, tryPrint: boolean): void {
    const url = URL.createObjectURL(blob);
    const newTab = window.open(url, "_blank");

    if (!newTab) {
        // Popup-Blocker hat den Tab blockiert (kann im Fallback-Pfad vorkommen)
        URL.revokeObjectURL(url);
        return;
    }

    if (tryPrint) {
        setTimeout(() => {
            try {
                newTab.focus();
                newTab.print();
            } catch (e) {
                // Browser-PDF-Viewer erlaubt kein print() von außen — Tab bleibt offen
            }
        }, 1000);
    }

    // Blob URL freigeben nachdem der Tab geladen hat
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Persistenter iframe für Safari — bleibt im DOM, damit Safari
// den Focus/Activation-Kontext bei wiederholtem Drucken bestmöglich behält.
let printIframe: HTMLIFrameElement | null = null;
let currentBlobUrl: string | null = null;

/**
 * Druckt über einen persistenten versteckten iframe (kein neuer Tab).
 * Wird nur für Safari verwendet — der einzige Browser, der
 * iframe.contentWindow.print() mit PDF-Inhalt korrekt unterstützt.
 * Bei Fehlern (z.B. SecurityError) → Fallback auf neuen Tab.
 */
function printViaIframe(blob: Blob): void {
    // Alte Blob URL freigeben
    if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
    }

    currentBlobUrl = URL.createObjectURL(blob);

    // Iframe erstellen falls noch nicht vorhanden oder aus DOM entfernt
    if (!printIframe || !printIframe.parentNode) {
        printIframe = document.createElement("iframe");
        // WICHTIG: NICHT display:none verwenden — Safari druckt sonst leere Seiten
        printIframe.style.position = "fixed";
        printIframe.style.right = "0";
        printIframe.style.bottom = "0";
        printIframe.style.width = "0";
        printIframe.style.height = "0";
        printIframe.style.border = "none";
        printIframe.style.overflow = "hidden";
        document.body.appendChild(printIframe);
    }

    // Event-Handler vor src-Änderung setzen (onload feuert bei jedem src-Wechsel)
    printIframe.onload = () => {
        // Minimale Verzögerung: Safari rendert PDFs nativ, daher ist der Inhalt
        // nach onload praktisch sofort verfügbar. Kurzer Timeout reicht und
        // hält Safaris User-Activation-Token (~5s) noch gültig.
        setTimeout(() => {
            try {
                if (!printIframe || !printIframe.contentWindow) {
                    throw new Error("contentWindow not available");
                }

                // Safari zeigt ohne focus() einen Bestätigungsdialog.
                // window.focus() holt den Fokus zurück aufs Hauptfenster, falls
                // ein anderer iframe (z.B. PDF-Vorschau) den Fokus hat.
                window.focus();
                printIframe.contentWindow.focus();
                printIframe.contentWindow.print();
            } catch (e) {
                // contentWindow.print() fehlgeschlagen (z.B. SecurityError)
                openInNewTab(blob, true);
            }
        }, 100);
    };

    printIframe.onerror = () => {
        openInNewTab(blob, true);
    };

    // src-Wechsel löst onload aus
    printIframe.src = currentBlobUrl;
}
